import React from 'react';
import { Check } from 'lucide-react';
import {
  STEP_LABELS,
  WizardSelection,
  WizardStepType,
} from '@/components/wizard/types';
import { subCategoryDisplayLabel } from './subCategoryLabels';

interface WizardBreadcrumbProps {
  selection: WizardSelection;
  hiddenSteps?: WizardStepType[];
}

interface Crumb {
  step: WizardStepType;
  text: string;
}

/**
 * One pill per answered step. Sub-category holds an array and variant a per-sub-category
 * map (OV-1180), so each step formats its own value rather than being rendered raw.
 */
const buildCrumbs = (
  selection: WizardSelection,
  hiddenSteps: WizardStepType[]
): Crumb[] => {
  const crumbs: Crumb[] = [];
  const visible = (step: WizardStepType) => !hiddenSteps.includes(step);

  if (selection.category && visible(WizardStepType.CATEGORY)) {
    crumbs.push({
      step: WizardStepType.CATEGORY,
      text: selection.category,
    });
  }

  const subCategories = selection.subCategory ?? [];
  if (subCategories.length > 0 && visible(WizardStepType.SUBCATEGORY)) {
    crumbs.push({
      step: WizardStepType.SUBCATEGORY,
      text: subCategories.map(subCategoryDisplayLabel).join(', '),
    });
  }

  const variants = Object.values(selection.variant ?? {});
  if (variants.length > 0 && visible(WizardStepType.VARIANT)) {
    crumbs.push({
      step: WizardStepType.VARIANT,
      text: variants.join(', '),
    });
  }

  return crumbs;
};

const WizardBreadcrumb: React.FC<WizardBreadcrumbProps> = ({
  selection,
  hiddenSteps = [],
}) => {
  const crumbs = buildCrumbs(selection, hiddenSteps);

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <nav aria-label="Wizard progress" role="navigation">
        <ol className="flex items-center gap-2 flex-wrap" role="list">
          {crumbs.map(crumb => (
            <div
              key={crumb.step}
              className="flex items-center space-x-2 bg-success-50 text-gray-700 px-3 py-1 rounded-full text-sm border border-success-200"
              role="listitem"
            >
              <span>
                {STEP_LABELS[crumb.step]}: {crumb.text}
              </span>
              <Check size={14} />
            </div>
          ))}
        </ol>
      </nav>
    </div>
  );
};

export default WizardBreadcrumb;
